import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getSitecoreComponentData } from '@services/sitecoreservice';
import { Tag } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/tag';
import { VehicleImageInfo } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/vehicleimageinfo';
import { VehicleTechnicalInfo } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/vehicletechnicaiInfo';
import { LeaseConfiguratorConfig, VehicleQuickDetails } from 'interface';
import type { RootState } from '../store';
import { PricingType } from '../../enums';
import { siteWidePersonalBusinessToggleSelector } from '../../constants';

export const initialState: VehicleQuickDetails = {
  id: 0,
  make: '',
  model: '',
  type: '',
  image: '',
  siteCoreDetails: {
    id: 0,
    tags: [],
    config: {
      whyLeaseWithZenAuto: {
        heading: '',
        items: [],
      },
      auth0Config: {
        auth0Enabled: false,
      },
    },
  },
  transmission: '',
  range: 0,
  engineSize: '',
  seats: 0,
  doors: 0,
  fuel: '',
  vehicleImageInfo: [],
  score: '',
  pricingType:
    (
      document.getElementById(
        siteWidePersonalBusinessToggleSelector
      ) as HTMLInputElement
    )?.checked === true
      ? PricingType.Business
      : PricingType.Personal,
  isVehicleQuotable: true,
};

var sitecoreComponentDataRequest: Promise<any>;

export const getSitecoreComponentDataAsync =
  createAsyncThunk<LeaseConfiguratorConfig>(
    'vehicleInformation/getSitecoreComponentDataAsync',
    async (): Promise<any> => {
      if (!sitecoreComponentDataRequest) {
        sitecoreComponentDataRequest = getSitecoreComponentData();
      }

      return sitecoreComponentDataRequest;
    }
  );

export const vehicleInformationSlice = createSlice({
  name: 'vehicleInformation',
  initialState: initialState,
  reducers: {
    updateVehicleInformation: (
      state,
      action: PayloadAction<VehicleQuickDetails>
    ) => {
      return { ...state, ...action.payload };
    },
    updateVehicleTechnical: (
      state,
      action: PayloadAction<VehicleTechnicalInfo>
    ) => {
      state.technicalSpec = action.payload;
    },
    updateVehicleSiteCoreDetailsTags: (
      state,
      action: PayloadAction<{ id: number; tags: Tag[] }>
    ) => {
      state.siteCoreDetails!.id = Number(action.payload.id);
      state.siteCoreDetails!.tags = action.payload.tags;
    },
    updateVehicleImageInfo: (
      state,
      action: PayloadAction<VehicleImageInfo[]>
    ) => {
      state.vehicleImageInfo = action.payload;
    },
    updateVehicleScore: (state, action: PayloadAction<string>) => {
      state.score = action.payload;
    },
    updateVehiclePricingType: (state, action: PayloadAction<PricingType>) => {
      state.pricingType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getSitecoreComponentDataAsync.fulfilled,
      (state, action) => {
        if (state.siteCoreDetails == null) {
          return;
        }
        state.siteCoreDetails.config = action.payload;
      }
    );
  },
});

export const {
  updateVehicleInformation,
  updateVehicleTechnical,
  updateVehicleSiteCoreDetailsTags,
  updateVehicleImageInfo,
  updateVehicleScore,
  updateVehiclePricingType,
} = vehicleInformationSlice.actions;

export const vehicleInformation = (state: RootState) =>
  state.vehicleInformation;

export default vehicleInformationSlice.reducer;
